import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box, Checkbox, FormControlLabel, TextField, Typography, Button,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

function ExpirationCertificatesReportForm({
  initialValues, onSubmit, title, info,
}) {
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    report: Yup.object({
      active: Yup.boolean(),
      emails: Yup.array()
        .of(Yup.string().email(t('invalid_email')).required(t('required')))
        .test('unique-emails', t('duplicate_emails'), (emails) => {
          if (!emails) return true;
          const uniqueEmails = new Set(emails);
          return uniqueEmails.size === emails.length;
        }),
    }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const handleAddEmail = () => {
    const newEmails = [...formik.values.report.emails, ''];
    formik.setFieldValue('report.emails', newEmails);
  };

  const handleRemoveEmail = (index) => {
    const newEmails = formik.values.report.emails.filter((_, i) => i !== index);
    formik.setFieldValue('report.emails', newEmails);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        mt: 2,
      }}
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <Typography component="span" variant="h6" sx={{ alignSelf: 'center' }}>
        {title}
      </Typography>
      <Typography
        component="span"
        variant="caption"
      >
        {info}
      </Typography>

      <FormControlLabel
        control={(
          <Checkbox
            name="report.active"
            checked={formik.values.report.active}
            onChange={formik.handleChange}
          />
          )}
        label="Active"
      />

      {formik.values.report.emails.map((email, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={`email-field-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TextField
            size="small"
            id={`report.emails.[${index}]`}
            name={`report.emails.[${index}]`}
            value={email}
            label={`${t('email')} ${index + 1}`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.report?.emails?.[index]
              && Boolean(formik.errors.report?.emails?.[index])}
            helperText={formik.touched.report?.emails?.[index]
              && formik.errors.report?.emails?.[index]}
          />
          <IconButton onClick={() => handleRemoveEmail(index)} size="small">
            <RemoveIcon />
          </IconButton>
        </Box>
      ))}

      <Button onClick={handleAddEmail} size="medium" variant="outlined" endIcon={<AddIcon />}>
        {t('admin.add_email')}
      </Button>

      <Button variant="contained" color="primary" type="submit" size="small">
        {t('save')}
      </Button>

    </Box>
  );
}

export default ExpirationCertificatesReportForm;
